import {
  Box,
  Button,
  Flex,
  Field,
  Input,
  NativeSelect,
  Stack,
} from "@chakra-ui/react";
import { Plus, Trash2 } from "lucide-react";
import { useFieldArray, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

import { Camelize } from "camelize-ts";

import {
  InvoiceTemplateBodySchema,
  InvoiceTemplateSchema,
} from "../../../generated";
import { useInvoiceTemplateUpdate } from "../../../hooks/useInvoiceTemplateUpdate";
import { useInvoiceTemplateCreate } from "../../../hooks/useInvoiceTemplateCreate";

const linesSchema = yup.object({
  id: yup.number().integer(),
  invoicedQuantity: yup.number().integer().required(),
  description: yup.string().required(),
  netUnitAmount: yup.number().required(),
  vatRatePercentage: yup.number().integer().required(),
});
const schema = yup
  .object({
    name: yup.string().required(),
    dueDays: yup.number().positive().integer().required(),
    lines: yup.array().of(linesSchema).required(),
  })
  .required();

export const InvoiceTemplateForm = ({
  invoiceTemplate,
}: {
  invoiceTemplate?: Camelize<InvoiceTemplateSchema>;
}) => {
  const invoiceCreateMutation = useInvoiceTemplateCreate();
  const invoiceUpdateMutation = useInvoiceTemplateUpdate(invoiceTemplate?.id);

  const {
    register,
    handleSubmit,
    formState: { errors: formStateErrors },
    watch,
    setValue,
    getValues,
    control,
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
    values: {
      name: invoiceTemplate?.name || "",
      // @ts-ignore
      dueDays: invoiceTemplate?.dueDays || "",
      // @ts-ignore
      lines: invoiceTemplate?.lines || [
        {
          invoicedQuantity: 1,
          description: "",
          // @ts-ignore
          netUnitAmount: undefined,
          vatRatePercentage: 25,
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "lines",
  });

  watch("lines");

  const onSubmit = (data: Camelize<InvoiceTemplateBodySchema>) => {
    if (invoiceTemplate?.id) {
      return invoiceUpdateMutation.mutate(data);
    }
    return invoiceCreateMutation.mutate(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack mt={8}>
        <Field.Root>
          <Field.Label>Navn på fakturamal</Field.Label>
          <Input {...register("name")} />
        </Field.Root>
        <Field.Root invalid={Boolean(formStateErrors["dueDays"])}>
          <Field.Label>Dager forfall</Field.Label>
          <Input {...register("dueDays")} placeholder="14" />
        </Field.Root>
      </Stack>
      <Box mt={4}>
        <Field.Root>
          <Field.Label>Fakturalinjer</Field.Label>
        </Field.Root>
        {fields.map((field, i) => {
          const invoiceTemplateLine = getValues("lines")?.[i];
          const errorLine = formStateErrors.lines?.[i];
          return (
            <Stack
              key={field.id}
              margin={4}
              direction="row"
              wrap={["wrap", "wrap", "inherit"]}
            >
              <Field.Root
                invalid={Boolean(errorLine?.invoicedQuantity)}
                minWidth={100}
                maxWidth={100}
              >
                <Field.Label>Antall</Field.Label>
                <Input
                  type="number"
                  {...register(`lines.${i}.invoicedQuantity`)}
                  placeholder="Antall"
                  min={1}
                  onChange={(e) => {
                    setValue(
                      `lines.${i}.invoicedQuantity`,
                      parseInt(e.target.value, 10),
                    );
                  }}
                />
              </Field.Root>
              <Field.Root
                invalid={Boolean(errorLine?.description)}
                minWidth={300}
                maxWidth={450}
              >
                <Field.Label>Beskrivelse</Field.Label>
                <Input
                  {...register(`lines.${i}.description`)}
                  placeholder="Beskrivelse"
                />
              </Field.Root>
              <Field.Root
                invalid={Boolean(errorLine?.netUnitAmount)}
                minWidth={150}
                maxWidth={150}
              >
                <Field.Label>Enhetspris</Field.Label>
                <Input
                  type="number"
                  {...register(`lines.${i}.netUnitAmount`)}
                  placeholder="100"
                />
              </Field.Root>
              <Field.Root
                invalid={Boolean(errorLine?.vatRatePercentage)}
                minWidth={75}
                maxWidth={100}
              >
                <Field.Label>MVA</Field.Label>
                <NativeSelect.Root>
                  <NativeSelect.Field
                    {...register(`lines.${i}.vatRatePercentage`)}
                  >
                    <option value={0}>0%</option>
                    <option value={15}>15%</option>
                    <option value={25}>25%</option>
                  </NativeSelect.Field>
                </NativeSelect.Root>
              </Field.Root>
              <Field.Root minWidth={100} maxWidth={100}>
                <Field.Label>Sum</Field.Label>
                <Input
                  disabled={true}
                  value={(
                    (invoiceTemplateLine?.invoicedQuantity || 0) *
                      (invoiceTemplateLine?.netUnitAmount || 0) *
                      (1 +
                        (invoiceTemplateLine?.vatRatePercentage || 0) / 100)
                  ).toFixed(2)}
                />
              </Field.Root>
              <Flex alignItems="flex-end">
                <Button
                  onClick={() => {
                    remove(i);
                  }}
                >
                  <Trash2 />
                </Button>
              </Flex>
            </Stack>
          );
        })}
        <Stack>
          <Button
            onClick={() =>
              append({
                invoicedQuantity: 1,
                description: "",
                // @ts-ignore
                netUnitAmount: undefined,
                vatRatePercentage: 25,
              })
            }
            variant="outline"
          >
            <Plus /> Legg til linje
          </Button>
          <Button type="submit">Lagre</Button>
        </Stack>
      </Box>
    </form>
  );
};
